/**
 * CSV / Excel 一括上書き — 「現在選択中のカタログ」の内訳商品を差し替える。
 *
 * 「Excel / CSV から取込」(ImportProductsDialog) との違いは 1 点だけ:
 *   - 取込 : upsertProductCatalog で **新規カタログを作って** items を投入
 *   - 上書き: 既存カタログ (catalogId) の items だけを差し替える
 * パースは両者とも catalogSheetParser を共有する。
 *
 * ★ mode='sync' を使う理由 (mode='replace' を使ってはいけない理由)
 *   replace は既存 items を全削除して作り直すため、商品ドキュメント ID が全部変わる。
 *   報酬セット (commission_sets/{id}/lists/{role}.items[].productId) は
 *   この商品ドキュメント ID を保持しているので、replace で上書きすると
 *   SA 報酬表から全行が消える (既知の orphan productId 問題)。
 *   sync は SKU をキーに既存ドキュメントを update するため、
 *   ID が維持され報酬設定の紐付けも「メーカー発注に含まない」フラグも生き残る。
 *
 * SKU は製品名から決定論的に生成されるため (catalogSheetParser.generateSku)、
 * 同じ製品名の行は前回取込と同じ SKU になり、正しく突合される。
 */
import { AlertTriangle, FileSpreadsheet, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { ParsedSheet } from '../catalogSheetParser';
import {
  BULK_IMPORT_MAX_ROWS,
  CatalogColumnMap,
  CatalogRowsPreview,
  CatalogSheetSummaryBar,
  readCatalogSheetFile,
  summarize,
  toImportItems,
} from '../CatalogSheetPreview';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import {
  useBulkImportToCatalog,
  useCatalogItems,
  type ProductCatalogRecord,
} from '@/hooks/useProductCatalogs';


interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalogId: string;
  /** 上書き対象カタログ (見出し表示用。未取得なら null 可) */
  catalog: ProductCatalogRecord | null;
}

export function CatalogCsvOverwriteDialog({
  open,
  onOpenChange,
  catalogId,
  catalog,
}: Props) {
  const bulkImport = useBulkImportToCatalog();
  const existing = useCatalogItems(open ? catalogId : null);

  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setParsed(null);
    setParsing(false);
  }, [open, catalogId]);

  const summary = useMemo(
    () => (parsed ? summarize(parsed.rows) : null),
    [parsed],
  );

  /**
   * シートに「製品カテゴリ」列が無い (= 旧フォーマット) 場合は、
   * productCategory キーを payload から落として既存値を温存する。
   * これをしないと、旧フォーマットの価格表で上書きした瞬間に
   * 既存商品の製品カテゴリが全件消える。
   */
  const sheetHasProductCategory = parsed?.columns.productCategory != null;
  const importItems = useMemo(
    () => (parsed ? toImportItems(parsed.rows, sheetHasProductCategory) : []),
    [parsed, sheetHasProductCategory],
  );

  /**
   * 上書きの実影響を SKU 突合で事前に見せる。
   * 「何件が更新され、何件が新規で、何件が消えるか」を実行前に確定表示することで、
   * 列ずれ / 別ブランドの価格表を誤って流し込む事故を実行前に止められる。
   */
  const impact = useMemo(() => {
    const existingSkus = new Set((existing.data ?? []).map((it) => it.sku));
    const incomingSkus = new Set(importItems.map((it) => it.sku));
    let updated = 0;
    for (const s of incomingSkus) if (existingSkus.has(s)) updated++;
    const created = incomingSkus.size - updated;
    let removed = 0;
    for (const s of existingSkus) if (!incomingSkus.has(s)) removed++;
    return {
      existingTotal: existingSkus.size,
      updated,
      created,
      removed,
      /** 既存が 0 件でないのに 1 件も一致しない = 別カタログのファイルを掴んでいる疑い */
      suspicious:
        existingSkus.size > 0 && incomingSkus.size > 0 && updated === 0,
    };
  }, [existing.data, importItems]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // 同じファイルを選び直せるようにリセット
    if (!f) return;
    setFile(f);
    setParsed(null);
    setParsing(true);
    try {
      const result = await readCatalogSheetFile(f);
      setParsed(result);
      if (result.rows.length === 0) {
        toast.error('取込可能な行が見つかりませんでした');
      } else {
        toast.success(`${result.rows.length} 件検出`);
      }
    } catch (err) {
      toast.error(`パース失敗: ${(err as Error).message}`);
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async () => {
    if (importItems.length === 0) {
      toast.error('有効な行がありません');
      return;
    }
    if (importItems.length > BULK_IMPORT_MAX_ROWS) {
      toast.error(
        `一度に取込めるのは ${BULK_IMPORT_MAX_ROWS} 件までです (${importItems.length} 件)`,
      );
      return;
    }
    const label = catalog
      ? `${catalog.category} / ${catalog.label || catalog.version}`
      : catalogId;
    if (
      !window.confirm(
        `カタログ「${label}」の内訳商品を上書きします。\n\n` +
          `・更新: ${impact.updated} 件 (商品IDは維持されます)\n` +
          `・新規追加: ${impact.created} 件\n` +
          `・削除: ${impact.removed} 件 (ファイルに存在しない商品)\n\n` +
          `この操作は取り消せません。実行しますか？`,
      )
    )
      return;

    try {
      const r = await bulkImport.mutateAsync({
        catalogId,
        mode: 'sync',
        items: importItems,
      });
      toast.success(
        `一括上書き完了: 更新 ${r.updated} 件 / 新規 ${r.created} 件 / 削除 ${r.deletedMissing} 件`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`上書き失敗: ${(err as Error).message}`);
    }
  };

  const catalogLabel = catalog
    ? `${catalog.category} / ${catalog.label || catalog.version}`
    : '選択中のカタログ';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>CSV / Excel 一括上書き</DialogTitle>
          <DialogDescription>
            <strong>{catalogLabel}</strong>{' '}
            の内訳商品を、選択したファイルの内容で差し替えます。新しいカタログは作成されません。
            SKU が一致する商品は<strong>商品 ID を維持したまま更新</strong>されるため、
            報酬設定の紐付けや「メーカー発注に含まない」設定は保持されます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ファイル選択 */}
          <div className="rounded-md border-2 border-dashed border-[var(--border)] bg-[var(--bg-elevated)] p-6 text-center">
            <FileSpreadsheet className="size-8 text-[var(--text-muted)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-secondary)] mb-3">
              {file
                ? file.name
                : 'Excel (.xlsx / .xls) または CSV (.csv) を選択してください'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,text/csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={parsing || bulkImport.isPending}
            >
              <Upload className="size-3.5" />
              {parsing ? 'パース中…' : file ? '別のファイルを選ぶ' : 'ファイルを選択'}
            </Button>
            <p className="text-[10px] text-[var(--text-muted)] mt-3">
              列はヘッダー名で自動判定します（製品名 / 製品カテゴリ / 仕入価格 (JPY) /
              税抜価格 (JPY) / 税込価格 (JPY) / 備考）。旧フォーマットも取込めます。
            </p>
          </div>

          {parsed && summary && (
            <>
              <CatalogColumnMap parsed={parsed} />
              <CatalogSheetSummaryBar
                summary={summary}
                onReset={() => {
                  setParsed(null);
                  setFile(null);
                }}
              />

              {/* 上書きの影響 (SKU 突合の事前計算) */}
              <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                  上書きの影響
                  {existing.isLoading && (
                    <span className="ml-2 font-normal normal-case text-[10px] text-[var(--text-muted)]">
                      既存商品を読込み中…
                    </span>
                  )}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <ImpactCell
                    label="現在の商品数"
                    value={impact.existingTotal}
                    tone="muted"
                  />
                  <ImpactCell label="更新 (ID維持)" value={impact.updated} tone="ok" />
                  <ImpactCell label="新規追加" value={impact.created} tone="accent" />
                  <ImpactCell label="削除" value={impact.removed} tone="danger" />
                </div>
                {impact.suspicious && (
                  <div className="flex items-start gap-1.5 text-[11px] text-[var(--accent-red)] rounded border border-[var(--accent-red)] bg-[var(--accent-red-subtle)] px-2 py-1.5">
                    <AlertTriangle className="size-3 shrink-0 mt-0.5" />
                    <span>
                      既存商品と SKU が 1 件も一致しません。別ブランドの価格表を選んでいないか、
                      列マッピングがずれていないか確認してください。このまま実行すると
                      現在の {impact.existingTotal} 件はすべて削除されます。
                    </span>
                  </div>
                )}
                {parsed && !sheetHasProductCategory && (
                  <p className="text-[11px] text-[var(--text-muted)] flex items-start gap-1.5">
                    <AlertTriangle className="size-3 shrink-0 mt-0.5" />
                    <span>
                      このファイルには「製品カテゴリ」列がありません。既存商品の製品カテゴリは
                      <strong>変更せずそのまま保持</strong>します（null で上書きしません）。
                    </span>
                  </p>
                )}
                {impact.removed > 0 && !impact.suspicious && (
                  <p className="text-[11px] text-[var(--accent-amber)] flex items-start gap-1.5">
                    <AlertTriangle className="size-3 shrink-0 mt-0.5" />
                    <span>
                      ファイルに存在しない {impact.removed}{' '}
                      件はカタログから削除されます。過去の見積行はスナップショット済みのため影響しません。
                    </span>
                  </p>
                )}
              </div>

              <CatalogRowsPreview rows={parsed.rows} maxHeightClass="max-h-[300px]" />
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={bulkImport.isPending}
          >
            キャンセル
          </Button>
          {summary && summary.valid > 0 && (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={bulkImport.isPending || existing.isLoading}
            >
              {bulkImport.isPending && <Spinner className="size-3 mr-1" />}
              {bulkImport.isPending
                ? '上書き中…'
                : `このカタログに上書き (${summary.valid} 件)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImpactCell({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'muted' | 'ok' | 'accent' | 'danger';
}) {
  const color =
    tone === 'ok'
      ? 'text-[var(--accent-green)]'
      : tone === 'accent'
        ? 'text-[var(--accent)]'
        : tone === 'danger'
          ? value > 0
            ? 'text-[var(--accent-red)]'
            : 'text-[var(--text-muted)]'
          : 'text-[var(--text-secondary)]';
  return (
    <div className="rounded border border-[var(--border)] bg-[var(--bg-input)] px-2 py-1.5">
      <div className="text-[10px] text-[var(--text-muted)]">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
